/**
 * admin-viewing-map.js
 */

import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import usePrevious from 'utils/use-previous';
import FeatureBox from 'components/feature-box';
import { TRACK_TYPE, processPoints } from '../tracking-common';
import { TrackContext } from '../track-context';
import { MODE, FILTERING_ALGO, AdminMapContext } from './admin-map-context';
import { 
  filterAccuracy,
  filterSpeedCalculated,
  filterAccuracyMovingAvg,
  RamerDouglasPeucker,
  Kalman, 
  defaultPointFilter
} from '../map/point-filtering';
import { TRACK_COLOR } from '../map/map-track';
import { MapBase } from '../map/viewing-map';


export default function AdminViewingMap(props) {
  const { setTracks, updateTrack } = React.useContext(TrackContext);
  const { 
    mode,
    userTrack,
    isLoading,
    isFollowTrackVisible,
    isLeadTrackVisible,
    displayedPointProperty,
    filtering: {
      algo,
      accuracyFilter,
      calculatedSpeedFilter,
      movingAvgSize,
      movingAvgDev,
      peuckerThreshold,
      kalmanAlgo,
    } 
  } = React.useContext(AdminMapContext);
  const userTrackRef = React.useRef();
  const [baseTracks, setBaseTracks] = React.useState([]);
  const prevMode = usePrevious(mode);


  // init from userTrack
  React.useEffect(() => {
    if (userTrack) {
      userTrackRef.current = userTrack;
      const baseTracks = [userTrack];
      if (userTrack.type === TRACK_TYPE.FOLLOW && userTrack.leadTrack) {
        baseTracks.push(userTrack.leadTrack);
      }
      setBaseTracks(baseTracks.map(track => ({ ...track, points: processPoints(track.points) })));
      setTracks(baseTracks);
    }
  }, [userTrack, setTracks]);


  // update lead/follow visibility
  React.useEffect(() => {    
    if (mode === MODE.VIEW
      && userTrackRef.current 
      && userTrackRef.current.type === TRACK_TYPE.FOLLOW 
      && userTrackRef.current.leadTrack
    ) {
      setBaseTracks(p => p.map(track => {
        if (track.type === TRACK_TYPE.FOLLOW) {
          return {
            ...track,
            isHidden: !isFollowTrackVisible
          };
        } else if (track.type === TRACK_TYPE.LEAD) {
          return {
            ...track,
            isHidden: !isLeadTrackVisible
          };
        } else {
          return track;
        }
      }));
      updateTrack(userTrackRef.current.id, { isHidden: !isFollowTrackVisible });
      updateTrack(userTrackRef.current.leadTrack.id, { isHidden: !isLeadTrackVisible });
    }
  }, [mode, updateTrack, isFollowTrackVisible, isLeadTrackVisible]);


  // update track overlay property
  React.useEffect(() => {
    setBaseTracks(p => p.map(track => ({
      ...track,
      displayedPointProperty
    })));
    if (userTrackRef.current) {
      updateTrack(userTrackRef.current.id, { displayedPointProperty });
      if (userTrackRef.current.leadTrack) {
        updateTrack(userTrackRef.current.leadTrack.id, { displayedPointProperty });
      }
    }
  }, [updateTrack, displayedPointProperty]);


  // do filtering
  React.useEffect(() => {
    if (mode === MODE.FILTER) {
      const baseTrack = {
        ...baseTracks[0],
        isHidden: false,
      };
      const filteredTrack = cloneDeep({
        ...baseTrack,
        id: `${baseTrack.id}-filtered`,
        isHidden: false,
        displayedPointProperty: undefined,
        color: TRACK_COLOR[TRACK_TYPE.MULTI_DEPLOYMENT][5],
      });

      if (algo === FILTERING_ALGO.DEFAULT) {
        filteredTrack.points = defaultPointFilter(filteredTrack.points);
      } else if (algo === FILTERING_ALGO.CUSTOM) {
        // accuracy
        if (accuracyFilter) {
          filteredTrack.points = filterAccuracy(filteredTrack.points, accuracyFilter);
        }

        // calculated speed
        if (calculatedSpeedFilter) {
          filteredTrack.points = filterSpeedCalculated(filteredTrack.points, calculatedSpeedFilter);
        }

        // accuracy moving average
        if (movingAvgSize && movingAvgDev) {
          filteredTrack.points = filterAccuracyMovingAvg(filteredTrack.points, movingAvgSize, movingAvgDev);
        }

        // peucker - threshold simplified to ints by dividing by 1bil
        if (peuckerThreshold) {
          filteredTrack.points = RamerDouglasPeucker(filteredTrack.points, +peuckerThreshold / 1000000000);
        }

        // kalman
        if (kalmanAlgo) {
          filteredTrack.points = Kalman(filteredTrack.points, kalmanAlgo);
        }
      }

      setTracks([baseTrack, filteredTrack], false);
    }
  }, [
    mode,
    baseTracks,
    algo, 
    accuracyFilter, 
    calculatedSpeedFilter, 
    movingAvgSize,
    movingAvgDev,
    kalmanAlgo, 
    peuckerThreshold, 
    setTracks,
  ]);


  // turn filtering off. 
  // layout effect ensures that changes to userTrack in filter mode render properly.
  React.useLayoutEffect(() => {
    if (mode !== MODE.FILTER && prevMode === MODE.FILTER) {
      setTracks(baseTracks);
    }
  }, [baseTracks, mode, prevMode, setTracks])


  return (
    <FeatureBox 
      loading={isLoading.userTrack}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {userTrack && <MapBase />}
    </FeatureBox>
  );
}